import { FC, MouseEventHandler, useReducer } from 'react';

import { useFormik } from 'formik';
import { Download, SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';

import css from './FileActionsColumn.module.css';

import { fileSystemService } from 'v2/api/fileSystemService';
import { UpdateFileForm } from 'v2/components/fileSystem/form';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { FileModel } from 'v2/types/FileSystemModel';
import { downloadFileFromLink, fileSrc, handleHttpError } from 'v2/utils';

interface FileActionsColumnProps {
  file: FileModel;
  onRevalidate: VoidFunction;
}

export const FileActionsColumn: FC<FileActionsColumnProps> = ({ file, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate } = useSWRMutation(['updateFile', file.id], fileSystemService.updateFile, {
    onSuccess: () => {
      toast.success('The file has been successfully updated');
      handleClose();
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerDelete, isMutating } = useSWRMutation(['deleteFile', file.id], fileSystemService.deleteFile, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<UpdateFileForm>({
    initialValues: { filename: file.name },
    onSubmit: (values) => {
      triggerUpdate(values.filename);
    },
    enableReinitialize: true,
  });

  const handleDownload = () => {
    downloadFileFromLink(fileSrc(file.id), file.name);
  };

  const handlePrevent: MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen();
  };

  return (
    <TableCell align="right" onClick={handlePrevent}>
      <div className={css.fileActions}>
        <IconButton color="primary" onClick={handleDownload}>
          <Download width={20} height={20} />
        </IconButton>

        <IconButton color="warning" onClick={setOpen}>
          <SquarePen width={20} height={20} />
        </IconButton>

        <Popconfirm message="Are you sure that you want to delete this file?" onConfirm={triggerDelete}>
          <IconButton color="error">
            <Trash2 width={20} height={20} />
          </IconButton>
        </Popconfirm>
      </div>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit File</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              name="filename"
              value={formik.values.filename}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="File Name"
              required
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Edit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </TableCell>
  );
};
